import { useEffect, useState } from 'react';

import {
  ButtonBase,
  MenuItem,
  OutlinedInput,
  Popover,
  Select,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import {
  GridColDef,
  gridFilterActiveItemsSelector,
  GridFilterItem,
  GridFilterModel,
  gridFilterModelSelector,
  GridFilterOperator,
  GridLogicOperator,
  GridSortDirection,
  GridSortModel,
  gridSortModelSelector,
  useGridApiRef,
} from '@mui/x-data-grid-premium';

import { Box } from '../box/box';
import { useWindowedTooltipZIndex } from '../dialog/windowed-tooltip-zindex-context';
import { useScaledMenuProps } from '../select/use-scaled-menu-props';
import { useTheme } from '../styles/styles';
import { PHONE_MEDIA_QUERY } from './phone-page-scroll';

type ApiRef = ReturnType<typeof useGridApiRef>;

// Grid props that decide what the old column menu offered.
export type SortFilterGridFlags = {
  disableColumnMenu?: boolean;
  disableColumnFilter?: boolean;
  disableColumnSorting?: boolean;
};

// Internal grid columns (checkbox, detail toggle, row actions) never count.
const isDataColumn = (col: Partial<GridColDef>) =>
  !!col.field && !col.field.startsWith('__') && col.type !== 'actions';

// Same rules as before: header click sorts any sortable column, and filters
// were only reachable from the column menu.
const isSortableColumn = (
  col: Partial<GridColDef>,
  flags: SortFilterGridFlags
) => isDataColumn(col) && !flags.disableColumnSorting && col.sortable !== false;

const isFilterableColumn = (
  col: Partial<GridColDef>,
  flags: SortFilterGridFlags
) =>
  isDataColumn(col) &&
  !flags.disableColumnMenu &&
  !flags.disableColumnFilter &&
  !col.disableColumnMenu &&
  col.filterable !== false;

export const getSortFilterSupport = (
  columns: readonly Partial<GridColDef>[],
  flags: SortFilterGridFlags
) => ({
  sort: columns.some((c) => isSortableColumn(c, flags)),
  filter: columns.some((c) => isFilterableColumn(c, flags)),
});

// Sort and filter models of a grid, kept in sync with its state (header
// clicks, the popover, persisted filters).
export const useGridSortFilterModels = (apiRef: ApiRef) => {
  const [models, setModels] = useState<{
    sortModel: GridSortModel;
    filterModel: GridFilterModel;
    active: number;
  }>({ sortModel: [], filterModel: { items: [] }, active: 0 });

  useEffect(() => {
    const read = () => {
      const sortModel = gridSortModelSelector(apiRef);
      const filterModel = gridFilterModelSelector(apiRef);
      const active = gridFilterActiveItemsSelector(apiRef).length;
      setModels((prev) =>
        prev.sortModel === sortModel &&
        prev.filterModel === filterModel &&
        prev.active === active
          ? prev
          : { sortModel, filterModel, active }
      );
    };
    // The grid can mount later than the button (collapsed section, loading
    // skeleton) or remount; follow whichever grid the ref points at.
    let api: ApiRef['current'] = null;
    let unsubscribe: (() => void) | undefined;
    const attach = () => {
      if (apiRef.current === api) return;
      unsubscribe?.();
      unsubscribe = undefined;
      api = apiRef.current;
      if (!api?.subscribeEvent) return;
      read();
      unsubscribe = api.subscribeEvent('stateChange', read);
    };
    attach();
    const timer = setInterval(attach, 1000);
    return () => {
      clearInterval(timer);
      unsubscribe?.();
    };
  }, [apiRef]);

  return models;
};

type Option = { value: unknown; label: string };

const columnLabel = (col: GridColDef) => col.headerName || col.field;

const isSelectColumn = (col: GridColDef) =>
  col.type === 'singleSelect' || col.type === 'boolean';

// Value choices of a select column; Yes / No columns are booleans.
const columnOptions = (col: GridColDef): Option[] => {
  if (col.type === 'boolean')
    return [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' },
    ];
  const raw = (col as { valueOptions?: unknown }).valueOptions;
  const list = (
    typeof raw === 'function' ? raw({ field: col.field }) : raw ?? []
  ) as unknown[];
  const select = col as {
    getOptionValue?: (o: unknown) => unknown;
    getOptionLabel?: (o: unknown) => string;
  };
  return list.map((o) => ({
    value:
      select.getOptionValue?.(o) ??
      (typeof o === 'object' && o ? (o as { value: unknown }).value : o),
    label: String(
      select.getOptionLabel?.(o) ??
        (typeof o === 'object' && o ? (o as { label: unknown }).label : o)
    ),
  }));
};

const fieldSx = (theme: Theme) => ({
  width: '100%',
  minWidth: 0,
  height: 38,
  borderRadius: '9px',
  background: theme.background.white,
  fontSize: '13.5px',
  color: theme.custom?.brandNavy,
  transition: 'box-shadow 120ms',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.custom?.controlBorder,
    borderWidth: '1px !important',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.custom?.controlBorder,
  },
  '&.Mui-focused': { boxShadow: `0 0 0 3px ${theme.custom?.fieldFocusRing}` },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.colors.primary.main,
  },
  '& .MuiInputBase-input': {
    height: 'auto',
    padding: '0 11px',
    lineHeight: '24px',
    '&::placeholder': { color: theme.custom?.fieldPlaceholder, opacity: 1 },
  },
  '& .MuiSelect-select': {
    minHeight: 'auto !important',
    paddingRight: '30px !important',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& .MuiSelect-icon': { color: theme.custom?.iconButton },
});

const FieldSelect = ({
  value,
  options,
  onChange,
  label,
  multiple,
  placeholder,
}: {
  value: string | string[];
  options: { value: string; label: string }[];
  onChange: (value: string | string[]) => void;
  label: string;
  multiple?: boolean;
  placeholder?: string;
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  // In a windowed dialog the menu sits above the popover it opens from.
  const layer = useWindowedTooltipZIndex();
  const { anchorRef, measure, menuProps } = useScaledMenuProps(
    {
      ...(layer && { sx: { zIndex: layer.zIndex + 1 } }),
      slotProps: {
        paper: {
          sx: {
            marginTop: '4px',
            borderRadius: '10px',
            boxShadow: theme.custom?.popoverShadow,
            '& .MuiMenuItem-root': {
              minHeight: 34,
              fontSize: '13.5px',
              color: theme.custom?.brandNavy,
            },
          },
        },
      },
    },
    open
  );
  const labelOf = (v: string) => options.find((o) => o.value === v)?.label;
  return (
    <Select
      ref={anchorRef}
      size="small"
      displayEmpty
      multiple={multiple}
      value={value}
      open={open}
      onOpen={() => {
        measure();
        setOpen(true);
      }}
      onClose={() => setOpen(false)}
      onChange={(e) => onChange(e.target.value as string | string[])}
      inputProps={{ 'aria-label': label }}
      MenuProps={menuProps}
      renderValue={(v) => {
        const text = Array.isArray(v)
          ? v.map(labelOf).filter(Boolean).join(', ')
          : labelOf(v);
        return text ? (
          text
        ) : (
          <Box component="span" sx={{ color: theme.custom?.fieldPlaceholder }}>
            {placeholder ?? 'Select'}
          </Box>
        );
      }}
      sx={fieldSx(theme)}
    >
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value}>
          {o.label}
        </MenuItem>
      ))}
    </Select>
  );
};

const FilterValue = ({
  col,
  op,
  item,
  onChange,
}: {
  col: GridColDef;
  op: GridFilterOperator;
  item: GridFilterItem;
  onChange: (value: unknown) => void;
}) => {
  const theme = useTheme();
  const multiple = op.value === 'isAnyOf';
  // Local text so typing never fights the grid's state updates.
  const [text, setText] = useState(() =>
    Array.isArray(item.value) ? item.value.join(', ') : item.value ?? ''
  );
  if (!op.InputComponent) return <Box />;

  if (isSelectColumn(col)) {
    const options = columnOptions(col);
    const indexOf = (v: unknown) =>
      String(options.findIndex((o) => String(o.value) === String(v)));
    const selected = multiple
      ? ((item.value as unknown[]) ?? []).map(indexOf)
      : item.value == null || item.value === ''
      ? ''
      : indexOf(item.value);
    return (
      <FieldSelect
        label="Value"
        placeholder="Any"
        multiple={multiple}
        value={selected}
        options={options.map((o, i) => ({ value: String(i), label: o.label }))}
        onChange={(v) =>
          onChange(
            Array.isArray(v)
              ? v.map((i) => options[+i].value)
              : v === ''
              ? undefined
              : options[+v].value
          )
        }
      />
    );
  }

  const type =
    multiple || col.type === 'string' || !col.type
      ? 'text'
      : col.type === 'number'
      ? 'number'
      : col.type === 'date'
      ? 'date'
      : col.type === 'dateTime'
      ? 'datetime-local'
      : 'text';
  return (
    <OutlinedInput
      size="small"
      type={type}
      value={text}
      placeholder={multiple ? 'Values, comma separated' : 'Value'}
      inputProps={{ 'aria-label': 'Value' }}
      onChange={(e) => {
        const next = e.target.value;
        setText(next);
        onChange(
          multiple
            ? next
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
            : next === ''
            ? undefined
            : next
        );
      }}
      sx={fieldSx(theme)}
    />
  );
};

const headingSx = (theme: Theme) => ({
  margin: 0,
  fontSize: '11.5px',
  fontWeight: 600,
  letterSpacing: '.04em',
  textTransform: 'uppercase' as const,
  color: theme.custom?.tableHeaderText,
});

const iconSx = {
  width: 14,
  height: 14,
  flex: 'none',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.9,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const footerButtonSx = (theme: Theme, primary: boolean) => ({
  height: 32,
  padding: '0 14px',
  borderRadius: '8px',
  fontSize: '13.5px',
  ...(primary
    ? {
        border: `1px solid ${theme.colors.primary.main}`,
        background: theme.colors.primary.main,
        color: theme.background.white,
        '&:hover': { background: theme.colors.primary.dark },
      }
    : {
        border: `1px solid ${theme.custom?.controlBorder}`,
        background: theme.background.white,
        color: theme.custom?.brandIndigo,
        '&:hover': { background: theme.custom?.listButtonHoverBg },
        '&.Mui-disabled': { opacity: 0.45 },
      }),
});

export const SortFilterPopover = ({
  apiRef,
  flags,
  anchorEl,
  onClose,
}: {
  apiRef: ApiRef;
  flags: SortFilterGridFlags;
  anchorEl: HTMLElement | null;
  onClose: () => void;
}) => {
  const theme = useTheme();
  const open = Boolean(anchorEl);
  const { sortModel, filterModel, active } = useGridSortFilterModels(apiRef);
  // Above a windowed (non-modal, raised) dialog.
  const layer = useWindowedTooltipZIndex();
  const api = apiRef.current;

  const columns = open && api ? api.getAllColumns() : [];
  const visible = new Set(
    open && api ? api.getVisibleColumns().map((c) => c.field) : []
  );
  const sort = sortModel[0];
  const sortCols = columns.filter(
    (c) =>
      (visible.has(c.field) && isSortableColumn(c, flags)) ||
      c.field === sort?.field
  );
  const filterCols = columns.filter(
    (c) => isFilterableColumn(c, flags) && c.filterOperators?.length
  );
  const items = filterModel.items;

  const opLabel = (op: GridFilterOperator) =>
    op.label ??
    api?.getLocaleText(
      `filterOperator${op.value.charAt(0).toUpperCase()}${op.value.slice(
        1
      )}` as never
    ) ??
    op.value;

  const setSort = (field: string, dir: GridSortDirection) =>
    api?.setSortModel(field ? [{ field, sort: dir }] : []);

  // One And / Or setting joins every row; saved filters keep theirs.
  const logic =
    filterModel.logicOperator === GridLogicOperator.Or
      ? GridLogicOperator.Or
      : GridLogicOperator.And;
  const setItems = (
    next: GridFilterItem[],
    logicOperator: GridLogicOperator = logic
  ) => api?.setFilterModel({ ...filterModel, items: next, logicOperator });

  const newItem = (col: GridColDef): GridFilterItem => ({
    id: Date.now(),
    field: col.field,
    operator: col.filterOperators?.[0]?.value ?? '',
    value: undefined,
  });

  const addFilter = () => {
    const used = new Set(items.map((i) => i.field));
    const col = filterCols.find((c) => !used.has(c.field)) ?? filterCols[0];
    if (col) setItems([...items, newItem(col)]);
  };

  const multiOp = items.some(
    (i) =>
      (filterCols.find((c) => c.field === i.field)?.filterOperators?.length ??
        0) > 1
  );
  const opWidth = multiOp ? 112 : 62;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: -6, horizontal: 'right' }}
      sx={layer && { zIndex: layer.zIndex }}
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-label': 'Sort and filter',
          sx: {
            width: 460,
            maxWidth: 'calc(100% - 24px)',
            padding: '14px',
            boxSizing: 'border-box',
            display: 'grid',
            gap: '12px',
            borderRadius: '12px',
            boxShadow: theme.custom?.columnsPopoverShadow,
            [PHONE_MEDIA_QUERY]: {
              left: '12px !important',
              width: 'calc(100% - 24px)',
              maxWidth: 'none',
              '&::before': {
                content: '""',
                justifySelf: 'center',
                width: 36,
                height: 4,
                margin: '-4px 0 0',
                borderRadius: '2px',
                background: theme.custom?.controlBorder,
              },
            },
          },
        } as object,
      }}
    >
      {sortCols.length > 0 && (
        <Box>
          <Box component="h4" sx={{ ...headingSx(theme), marginBottom: '6px' }}>
            Sort by
          </Box>
          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <FieldSelect
                label="Sort column"
                value={sort?.field ?? ''}
                options={[
                  { value: '', label: 'None' },
                  ...sortCols.map((c) => ({
                    value: c.field,
                    label: columnLabel(c),
                  })),
                ]}
                onChange={(v) => setSort(v as string, sort?.sort ?? 'asc')}
              />
            </Box>
            <Box
              role="radiogroup"
              aria-label="Sort direction"
              sx={{
                display: 'inline-flex',
                flexShrink: 0,
                gap: '2px',
                padding: '3px',
                borderRadius: '9px',
                background: theme.custom?.fieldHoverBg,
              }}
            >
              {(['asc', 'desc'] as const).map((dir) => {
                const on = sort?.sort === dir;
                return (
                  <ButtonBase
                    key={dir}
                    role="radio"
                    aria-checked={on}
                    disabled={!sort}
                    onClick={() => sort && setSort(sort.field, dir)}
                    sx={{
                      height: 32,
                      padding: '0 12px',
                      gap: '5px',
                      borderRadius: '7px',
                      fontSize: '13px',
                      whiteSpace: 'nowrap',
                      color: theme.custom?.shellSectionTabText,
                      '&:hover': { color: theme.custom?.brandNavy },
                      ...(on && {
                        background: theme.background.white,
                        color: theme.custom?.shellMenuItemCurrentText,
                        fontWeight: 600,
                        boxShadow: theme.custom?.shellSectionTabShadow,
                      }),
                      '&.Mui-disabled': {
                        color: theme.custom?.columnsLockedHint,
                      },
                      [PHONE_MEDIA_QUERY]: { padding: '0 10px' },
                    }}
                  >
                    <Box component="svg" viewBox="0 0 24 24" sx={iconSx}>
                      <path
                        d={
                          dir === 'asc'
                            ? 'M12 19V5M6 11l6-6 6 6'
                            : 'M12 5v14M6 13l6 6 6-6'
                        }
                      />
                    </Box>
                    {dir === 'asc' ? 'Asc' : 'Desc'}
                  </ButtonBase>
                );
              })}
            </Box>
          </Box>
        </Box>
      )}
      {filterCols.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gap: '8px',
            ...(sortCols.length > 0 && {
              borderTop: `1px solid ${theme.custom?.windowedHeaderLine}`,
              paddingTop: '12px',
            }),
          }}
        >
          <Box component="h4" sx={headingSx(theme)}>
            Filter
          </Box>
          {items.map((item, index) => {
            const col =
              filterCols.find((c) => c.field === item.field) ?? filterCols[0];
            const ops = col.filterOperators ?? [];
            const op = ops.find((o) => o.value === item.operator) ?? ops[0];
            const update = (patch: Partial<GridFilterItem>) =>
              setItems(
                items.map((it, i) => (i === index ? { ...it, ...patch } : it))
              );
            return (
              <Box
                key={item.id ?? `${index}-${item.field}`}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `44px minmax(0, 1fr) ${opWidth}px minmax(0, 1fr) 28px`,
                  gap: '6px',
                  alignItems: 'center',
                  [PHONE_MEDIA_QUERY]: {
                    gridTemplateColumns: `44px ${opWidth}px minmax(0, 1fr) 28px`,
                    paddingBottom: '6px',
                    borderBottom: `1px solid ${theme.custom?.punchLineDivider}`,
                    '& > div:nth-of-type(1)': { gridColumn: 1, gridRow: 1 },
                    '& > div:nth-of-type(2)': {
                      gridColumn: '2 / 4',
                      gridRow: 1,
                    },
                    '& > div:nth-of-type(3)': { gridColumn: 2, gridRow: 2 },
                    '& > div:nth-of-type(4)': { gridColumn: 3, gridRow: 2 },
                    '& > button': { gridColumn: 4, gridRow: 1 },
                  },
                }}
              >
                {index ? (
                  <Box>
                    <ButtonBase
                      aria-label={
                        logic === GridLogicOperator.Or
                          ? 'Match any filter (switch to match all)'
                          : 'Match all filters (switch to match any)'
                      }
                      title="Match all / Match any"
                      onClick={() =>
                        setItems(
                          items,
                          logic === GridLogicOperator.Or
                            ? GridLogicOperator.And
                            : GridLogicOperator.Or
                        )
                      }
                      sx={{
                        height: 26,
                        gap: '2px',
                        margin: '0 0 0 -6px',
                        padding: '0 4px 0 6px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        color: theme.custom?.shellSectionTabText,
                        '&:hover': {
                          background: theme.custom?.columnsRowHoverBg,
                          color: theme.custom?.brandNavy,
                        },
                      }}
                    >
                      {logic === GridLogicOperator.Or ? 'Or' : 'And'}
                      <Box
                        component="svg"
                        viewBox="0 0 24 24"
                        aria-hidden
                        sx={{ ...iconSx, width: 12, height: 12 }}
                      >
                        <path d="m6 9 6 6 6-6" />
                      </Box>
                    </ButtonBase>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      fontSize: '12px',
                      color: theme.custom?.tableHeaderText,
                    }}
                  >
                    Where
                  </Box>
                )}
                <Box sx={{ minWidth: 0 }}>
                  <FieldSelect
                    label="Column"
                    value={col.field}
                    options={filterCols.map((c) => ({
                      value: c.field,
                      label: columnLabel(c),
                    }))}
                    onChange={(field) => {
                      const next = filterCols.find((c) => c.field === field);
                      if (next)
                        setItems(
                          items.map((it, i) =>
                            i === index ? { ...newItem(next), id: it.id } : it
                          )
                        );
                    }}
                  />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  {ops.length > 1 ? (
                    <FieldSelect
                      label="Operator"
                      value={op.value}
                      options={ops.map((o) => ({
                        value: o.value,
                        label: opLabel(o),
                      }))}
                      onChange={(operator) =>
                        update({
                          operator: operator as string,
                          value: undefined,
                        })
                      }
                    />
                  ) : (
                    <Box
                      sx={{
                        height: 38,
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: '8px',
                        background: theme.custom?.columnsRowHoverBg,
                        fontSize: '12.5px',
                        color: theme.custom?.shellSectionTabText,
                      }}
                    >
                      {opLabel(op)}
                    </Box>
                  )}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <FilterValue
                    key={`${item.field}-${item.operator}`}
                    col={col}
                    op={op}
                    item={item}
                    onChange={(value) => update({ value })}
                  />
                </Box>
                <ButtonBase
                  aria-label="Remove filter"
                  onClick={() => setItems(items.filter((_, i) => i !== index))}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '6px',
                    color: theme.custom?.tableHeaderText,
                    '&:hover': {
                      background: theme.custom?.columnsRowHoverBg,
                      color: theme.palette.error.main,
                    },
                  }}
                >
                  <Box component="svg" viewBox="0 0 24 24" sx={iconSx}>
                    <path d="M6 6l12 12M18 6 6 18" />
                  </Box>
                </ButtonBase>
              </Box>
            );
          })}
          <ButtonBase
            onClick={addFilter}
            sx={{
              justifySelf: 'start',
              gap: '6px',
              padding: '4px 0',
              fontSize: '13px',
              fontWeight: 500,
              color: theme.colors.primary.main,
              '&:hover': { color: theme.custom?.shellMenuItemCurrentText },
            }}
          >
            <Box component="svg" viewBox="0 0 24 24" sx={iconSx}>
              <path d="M12 5v14M5 12h14" />
            </Box>
            Add filter
          </ButtonBase>
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '8px',
          paddingTop: '10px',
          borderTop: `1px solid ${theme.custom?.windowedHeaderLine}`,
        }}
      >
        <ButtonBase
          disabled={!sortModel.length && !items.length && !active}
          onClick={() => {
            api?.setSortModel([]);
            setItems([], GridLogicOperator.And);
          }}
          sx={footerButtonSx(theme, false)}
        >
          Reset
        </ButtonBase>
        <ButtonBase onClick={onClose} sx={footerButtonSx(theme, true)}>
          Done
        </ButtonBase>
      </Box>
    </Popover>
  );
};
